import type { Variant, VariantAttribute } from '../types/onboarding';

export type OnboardingVariantOption = {
  id: string;
  label: string;
  imageUrl?: string;
};

export type OnboardingVariantGroup = {
  id: string;
  title: string;
  options: OnboardingVariantOption[];
};

export type OnboardingVariantRow = {
  key: string;
  label: string;
  price: string;
  available: boolean;
  source?: Partial<Variant> & Record<string, unknown>;
};

export type OnboardingVariantCombination = {
  key: string;
  label: string;
  selections: Array<{
    group: OnboardingVariantGroup;
    option: OnboardingVariantOption;
  }>;
};

const normalizeText = (value: string) => value.trim().toLowerCase();

const toSlug = (value: string) =>
  normalizeText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'opcion';

const toMoneyString = (value: unknown, fallback: number) => {
  const numericValue = Number(value);
  const nextValue = Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
  return nextValue > 0 ? String(nextValue) : '';
};

const toMoneyNumber = (value: unknown, fallback: number) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : fallback;
};

const getVariantProviderId = (variant: Record<string, unknown> | undefined) =>
  (variant?.provider_id as string | undefined) ?? (variant?.providerId as string | undefined);

const getVariantExternalId = (variant: Record<string, unknown> | undefined) =>
  (variant?.external_id as string | undefined) ?? (variant?.externalId as string | undefined);

const getVariantExternalSellPrice = (variant: Record<string, unknown> | undefined) =>
  (variant?.external_sell_price as number | undefined) ?? (variant?.externalSellPrice as number | undefined);

const getVariantExternalSuggestedSellPrice = (variant: Record<string, unknown> | undefined) =>
  (variant?.external_suggested_sell_price as number | undefined) ??
  (variant?.externalSuggestedSellPrice as number | undefined);

const getAttributeValue = (attribute: VariantAttribute | Record<string, any> | undefined) => {
  if (!attribute) return '';
  return String(attribute.color?.name ?? attribute.value ?? '').trim();
};

const getAttributeImageUrl = (attribute: VariantAttribute | Record<string, any> | undefined) => {
  if (!attribute) return '';
  return String((attribute as any).image_url ?? (attribute as any).imageUrl ?? '').trim();
};

const getCombinationKey = (labels: string[]) =>
  labels.map((label) => normalizeText(label)).join('||');

const makeOptionId = (groupIndex: number, optionIndex: number, label: string) =>
  `group-${groupIndex + 1}-option-${optionIndex + 1}-${toSlug(label || String(optionIndex + 1))}`;

export const createOnboardingVariantGroup = (index: number): OnboardingVariantGroup => ({
  id: `group-${index + 1}-${Date.now()}`,
  title: '',
  options: [
    { id: `group-${index + 1}-option-1-${Date.now()}`, label: '' },
    { id: `group-${index + 1}-option-2-${Date.now()}`, label: '' },
  ],
});

export const buildOnboardingVariantDraft = (
  variants: Variant[] | null | undefined,
  basePrice: number
) => {
  if (!variants?.length) {
    return {
      groups: [] as OnboardingVariantGroup[],
      rows: {} as Record<string, OnboardingVariantRow>,
    };
  }

  const groupTitles: string[] = [];
  variants.forEach((variant) => {
    (variant.attributes || []).slice(0, 2).forEach((attribute) => {
      const title = String(attribute.name || '').trim();
      if (title && !groupTitles.includes(title)) {
        groupTitles.push(title);
      }
    });
  });

  if (groupTitles.length === 0) {
    groupTitles.push('Variación');
  }

  const optionLabelsByGroup = new Map<string, string[]>();
  const optionImageByGroupAndLabel = new Map<string, string>();
  groupTitles.slice(0, 2).forEach((title) => optionLabelsByGroup.set(title, []));

  const rows: Record<string, OnboardingVariantRow> = {};

  variants.forEach((variant) => {
    const labels = groupTitles.slice(0, 2).map((title) => {
      const attribute = (variant.attributes || []).find((item) => item.name === title);
      return getAttributeValue(attribute);
    });

    const resolvedLabels = labels.every(Boolean)
      ? labels
      : [variant.name || variant.variant_key || 'Opción'];

    resolvedLabels.forEach((label, index) => {
      const title = groupTitles[index] || 'Variación';
      const options = optionLabelsByGroup.get(title) || [];
      if (!options.some((existingLabel) => normalizeText(existingLabel) === normalizeText(label))) {
        options.push(label);
      }
      const attribute = (variant.attributes || []).find((item) => item.name === title);
      const imageUrl = getAttributeImageUrl(attribute);
      const imageKey = `${title}::${normalizeText(label)}`;
      if (imageUrl && !optionImageByGroupAndLabel.has(imageKey)) {
        optionImageByGroupAndLabel.set(imageKey, imageUrl);
      }
      optionLabelsByGroup.set(title, options);
    });

    const rowKey = getCombinationKey(resolvedLabels);
    const source = variant as Partial<Variant> & Record<string, unknown>;
    rows[rowKey] = {
      key: rowKey,
      label: resolvedLabels.join(' - '),
      price: toMoneyString(variant.price, basePrice),
      available: variant.available ?? true,
      source,
    };
  });

  const groups = groupTitles.slice(0, 2).map((title, groupIndex) => ({
    id: `group-${groupIndex + 1}-${toSlug(title)}`,
    title,
    options: (optionLabelsByGroup.get(title) || []).map((label, optionIndex) => ({
      id: makeOptionId(groupIndex, optionIndex, label),
      label,
      imageUrl: optionImageByGroupAndLabel.get(`${title}::${normalizeText(label)}`),
    })),
  }));

  return { groups, rows };
};

export const getOnboardingVariantCombinations = (
  groups: OnboardingVariantGroup[]
): OnboardingVariantCombination[] => {
  const activeGroups = groups
    .slice(0, 2)
    .map((group) => ({
      ...group,
      title: group.title.trim(),
      options: group.options
        .map((option) => ({ ...option, label: option.label.trim() }))
        .filter((option) => option.label.length > 0),
    }))
    .filter((group) => group.title.length > 0 && group.options.length > 0);

  if (activeGroups.length === 0) return [];

  const combinations = activeGroups.reduce<OnboardingVariantCombination[]>(
    (acc, group) => {
      if (acc.length === 0) {
        return group.options.map((option) => ({
          key: getCombinationKey([option.label]),
          label: option.label,
          selections: [{ group, option }],
        }));
      }

      return acc.flatMap((combination) =>
        group.options.map((option) => {
          const selections = [...combination.selections, { group, option }];
          const labels = selections.map((selection) => selection.option.label);
          return {
            key: getCombinationKey(labels),
            label: labels.join(' - '),
            selections,
          };
        })
      );
    },
    []
  );

  return combinations;
};

export const buildOnboardingVariants = ({
  groups,
  rows,
  productName,
  basePrice,
  productImages,
  providerId,
}: {
  groups: OnboardingVariantGroup[];
  rows: Record<string, OnboardingVariantRow>;
  productName: string;
  basePrice: number;
  productImages: string[];
  providerId?: string | null;
}): Variant[] | null => {
  const combinations = getOnboardingVariantCombinations(groups);
  if (combinations.length === 0) return null;

  return combinations.map((combination, index) => {
    const row = rows[combination.key];
    const source = row?.source;
    const sourceProviderId = getVariantProviderId(source);
    const externalSellPrice = getVariantExternalSellPrice(source);
    const externalSuggestedSellPrice = getVariantExternalSuggestedSellPrice(source);
    const variantKeyParts = combination.selections.flatMap(({ group, option }) => [
      toSlug(group.title),
      toSlug(option.label),
    ]);
    const attributes: VariantAttribute[] = combination.selections.map(({ group, option }) => ({
      name: group.title.trim(),
      type_variation: 'generic',
      value: option.label.trim(),
      ...(option.imageUrl ? { image_url: option.imageUrl } : {}),
    } as VariantAttribute));
    const optionImages = combination.selections
      .map(({ option }) => option.imageUrl)
      .filter(Boolean) as string[];

    const variant: Variant = {
      name: combination.label || productName,
      variant_key: `${String(index).padStart(4, '0')}-${variantKeyParts.join('-')}`,
      price: toMoneyNumber(row?.price, basePrice),
      available: row?.available ?? true,
      images: Array.isArray(source?.images) && source.images.length > 0
        ? source.images
        : optionImages.length > 0
          ? optionImages
          : productImages,
      attributes,
      provider_id: sourceProviderId ?? providerId ?? null,
      external_id: getVariantExternalId(source) ?? null,
      external_sell_price: externalSellPrice ?? null,
      external_suggested_sell_price: externalSuggestedSellPrice ?? null,
    };

    return variant;
  });
};
